import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../../components/ui/Badge';
import type { InstanceKeyStatus } from '../../instanceTypes';

interface KeyStatusBadgeProps {
  /**
   * A known key status, `'UNKNOWN'` for an unrecognised value (rendered as an
   * "info" badge), or `null` when the instance has no key (muted "no key" badge).
   */
  status: InstanceKeyStatus | null;
}

type BadgeColors = { color: string; bgcolor: string; dot: string };

export function KeyStatusBadge({ status }: KeyStatusBadgeProps) {
  const theme = useTheme();
  const { t } = useTranslation('instances');

  const colorMap: Record<InstanceKeyStatus, BadgeColors> = {
    // "Générée" / "Generated" — key issued but not yet activated.
    ACTIVE: {
      color: theme.palette.info.dark,
      bgcolor: alpha(theme.palette.info.main, 0.1),
      dot: theme.palette.info.dark,
    },
    EXPIRED: {
      color: theme.palette.warning.dark,
      bgcolor: alpha(theme.palette.warning.main, 0.1),
      dot: theme.palette.warning.dark,
    },
    // "Activée" / "Activated" — key has been consumed by an instance.
    CONSUMED: {
      color: theme.palette.success.dark,
      bgcolor: alpha(theme.palette.success.main, 0.1),
      dot: theme.palette.success.dark,
    },
    REVOKED: {
      color: theme.palette.error.dark,
      bgcolor: alpha(theme.palette.error.main, 0.1),
      dot: theme.palette.error.dark,
    },
    // Unrecognised value from the API — flagged visibly rather than mislabelled.
    UNKNOWN: {
      color: theme.palette.info.dark,
      bgcolor: alpha(theme.palette.info.main, 0.1),
      dot: theme.palette.info.dark,
    },
  };

  // No key yet: neutral/muted badge with a meaningful label (accessible name),
  // instead of a bare em dash.
  const noKeyColors: BadgeColors = {
    color: theme.palette.text.disabled,
    bgcolor: alpha(theme.palette.text.disabled, 0.1),
    dot: theme.palette.text.disabled,
  };

  const { color, bgcolor, dot } = status ? colorMap[status] : noKeyColors;
  const label = t(`keys.statusLabels.${status ?? 'NONE'}`);

  return (
    <Badge
      label={label}
      color={color}
      bgcolor={bgcolor}
      dot={dot}
      fontWeight={theme.typography.fontWeightSemiBold}
    />
  );
}
